<template>
  <div class="w-full mixed-chart-container h-120">
    <canvas ref="mixedChart" class="w-full h-full"></canvas>
  </div>
</template>

<script>
import Chart from "chart.js";

export default {
  name: "MixedChart",
  props: {
    chartData: {
      type: Object,
      required: true
    },
    chartOptions: {
      type: Object,
      default: () => ({})
    },
    chartGridShow: {
      type: Boolean,
      default: true
    },
    width: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      mixedChart: null,
      chartWidth: null,
      multiArbitraryLine: {
        id: "multiArbitraryLine",
        beforeDatasetsDraw(chart, args, options) {
          const {
            ctx,
            chartArea: { top, bottom },
            scales
          } = chart;
          const x = scales["x-axis-0"];
          const { indexes = [] } = options;
          ctx.save();

          function drawLines(indexPosition) {
            const xPosition = x.getPixelForValue(null, indexPosition);
            //
            ctx.strokeStyle = "#E4E4E4";
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.setLineDash([10, 10]);
            ctx.moveTo(xPosition, top);
            ctx.lineTo(xPosition, bottom);
            ctx.stroke();
            ctx.closePath();
            ctx.restore();
            ctx.setLineDash([]);
          }

          for (let index = 0; index < indexes.length; index++) {
            drawLines(indexes[index]);
          }
        }
      }
    };
  },
  computed: {
    processedChartData() {
      return this.chartData;
    },
    defaultOptions() {
      const { labels = [], recorded_by: recordedBy = [] } = this.chartData;
      const phaseLineIndexes = [];
      labels.forEach((i, idx) => {
        if (!i) phaseLineIndexes.push(idx);
      });

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: {},
        legend: { display: false },
        tooltips: {
          usePointStyle: true,
          callbacks: {
            label: function(tooltipItem, data) {
              let dataset = data.datasets[tooltipItem.datasetIndex];
              let value = dataset.data[tooltipItem.index];
              return `${dataset.label}: ${value}`;
            },
            afterTitle: function(context) {
              if (recordedBy && recordedBy.at(context[0].index)) {
                return `Recorded by: ${recordedBy.at(context[0].index)}`;
              }
            }
          }
        },
        layout: { padding: { top: 6 } },
        scales: {
          yAxes: [
            {
              id: "y-axis-line",
              position: "left",
              stacked: false,
              ticks: {
                beginAtZero: true,
                display: true,
                max: 100,
                fontColor: "#707070",
                fontSize: 12,
                padding: 10
              },
              gridLines: {
                display: this.chartGridShow,
                drawBorder: true,
                tickMarkLength: 10
              }
            },
            {
              id: "y-axis-bar",
              position: "right",
              stacked: true,
              beginAtZero: true,
              ticks: {
                beginAtZero: true,
                display: true,
                // max: 10,
                fontColor: "#707070",
                fontSize: 12,
                padding: 10,
                callback: function(value) {
                  return value % 2 === 0 ? value : null;
                }
              },
              gridLines: {
                display: false,
                drawBorder: true
              }
            }
          ],
          xAxes: [
            {
              position: "bottom",
              offset: false,
              stacked: true,
              ticks: {
                fontColor: "#707070",
                fontSize: 12
              },
              gridLines: {
                display: this.chartGridShow,
                drawBorder: true
              }
            }
          ]
        },
        plugins: {
          multiArbitraryLine: {
            indexes: phaseLineIndexes
          },
          centerLabels: {
            enabled: true
          }
        }
      };
    },
    finalOptions() {
      return { ...this.defaultOptions, ...this.chartOptions };
    }
  },
  mounted() {
    this.updateChartContainerSize();
    this.initMixedChart();

    // Add resize listener to handle responsive behavior
    window.addEventListener("resize", this.handleResize);
  },
  methods: {
    updateChartContainerSize() {
      if (this.width) {
        this.chartWidth = typeof this.width === "number" ? `${this.width}px` : this.width;
      } else {
        // Use parent width if no width is specified
        const parentWidth = this.$el.parentElement ? this.$el.parentElement.offsetWidth : null;
        if (parentWidth) {
          this.chartWidth = `${parentWidth}px`;
        }
      }

      // Apply width to container if needed
      if (this.chartWidth && this.$el) {
        this.$el.style.width = this.chartWidth;
      }
    },
    handleResize() {
      this.updateChartContainerSize();
      if (this.mixedChart) {
        this.mixedChart.resize();
      }
    },
    initMixedChart() {
      if (this.$refs.mixedChart) {
        Chart.plugins.register(this.multiArbitraryLine);

        // Make sure we're working with the latest container size
        this.updateChartContainerSize();

        this.mixedChart = new Chart(this.$refs.mixedChart, {
          type: "bar",
          data: this.processedChartData,
          options: this.finalOptions
        });
      }
    },
    updateChart() {
      if (this.mixedChart) {
        this.mixedChart.data = this.processedChartData;
        this.mixedChart.options = this.finalOptions;

        const labels = this.chartData.labels || [];
        this.mixedChart.data.datasets.forEach(dataset => {
          if (dataset.type === "line") {
            if (dataset.data.length < labels.length) {
              const lastValue = dataset.data[dataset.data.length - 1];
              while (dataset.data.length < labels.length) {
                dataset.data.push(lastValue);
              }
            }
          }
        });

        this.mixedChart.update();
      }
    }
  },
  watch: {
    chartOptions: {
      deep: true,
      handler() {
        this.updateChart();
      }
    },
    width: {
      handler() {
        this.updateChartContainerSize();
        if (this.mixedChart) {
          this.mixedChart.resize();
        }
      }
    }
  },
  beforeDestroy() {
    // Clean up event listeners
    window.removeEventListener("resize", this.handleResize);

    if (this.mixedChart) {
      this.mixedChart.destroy();
    }
  }
};
</script>

<style scoped>
.mixed-chart-container {
  position: relative;
  box-sizing: border-box;
}
</style>



// WEBPACK FOOTER //
// src/components/charts/MixedChart.vue